import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Loader2, Users, MessageCircle, Sparkles, Plus, Trash2, Lock, ArrowLeft,
  Heart, Eye, Sprout, Zap, Power,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const PILLARS = [
  { key: "gratitude", label: "Gratitude", color: "#D98E73" },
  { key: "awareness", label: "Awareness", color: "#144F75" },
  { key: "unity", label: "Unity", color: "#8FA89B" },
  { key: "growth", label: "Growth", color: "#5B8A72" },
  { key: "energy", label: "Energy", color: "#B5794E" },
];
const pillarMeta = (k) => PILLARS.find((p) => p.key === k) || PILLARS[1];

export default function GuideCoachDashboard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("reflections");
  const [newPrompt, setNewPrompt] = useState({ text: "", pillar: "gratitude" });
  const [saving, setSaving] = useState(false);

  const isGuide = user && (user.is_certified_guide || ["admin", "staff"].includes(user.role));

  const { data, isLoading, isError } = useQuery({
    queryKey: ["guide-coach"],
    queryFn: () => api.get("/guide/coach/dashboard").then((r) => r.data),
    enabled: !!isGuide,
  });

  if (!isGuide) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6" data-testid="guide-coach-locked">
        <div className="eyw-card p-10 text-center max-w-md">
          <Lock size={30} className="mx-auto text-[#8FA89B] mb-3" />
          <h2 className="font-serif text-2xl text-[#1A242B]">Certified guides only</h2>
          <p className="text-[#4A5D6B] mt-2">This space is for certified guides. If you'd like to become one, reach out to the Council.</p>
          <Link to="/coach" className="eyw-btn-outline mt-5 inline-flex"><ArrowLeft size={16} /> Back to Daily Coach</Link>
        </div>
      </div>
    );
  }

  const addPrompt = async () => {
    if (!newPrompt.text.trim()) { toast.error("Write a prompt first."); return; }
    setSaving(true);
    try {
      await api.post("/guide/coach/prompts", { text: newPrompt.text.trim(), pillar: newPrompt.pillar, active: true });
      toast.success("Prompt published to your members");
      setNewPrompt({ text: "", pillar: "gratitude" });
      qc.invalidateQueries({ queryKey: ["guide-coach"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (p) => {
    try { await api.put(`/guide/coach/prompts/${p.id}`, { text: p.text, pillar: p.pillar, active: !p.active }); toast.success(p.active ? "Prompt hidden" : "Prompt activated"); qc.invalidateQueries({ queryKey: ["guide-coach"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/guide/coach/prompts/${id}`); toast.success("Prompt deleted"); qc.invalidateQueries({ queryKey: ["guide-coach"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (isLoading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" size={32} /></div>;
  if (isError || !data) return <div className="min-h-[60vh] grid place-items-center text-[#4A5D6B]">Couldn't load your guide dashboard.</div>;

  const s = data.stats || {};

  return (
    <div data-testid="guide-coach-dashboard" className="min-h-[70vh]">
      <section className="bg-[#144F75] py-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <p className="eyw-label !text-[#D98E73] mb-2 flex items-center gap-1.5"><Sparkles size={13} /> Certified Guide</p>
          <h1 className="font-serif text-4xl text-white">Guide Dashboard</h1>
          <p className="text-white/80 mt-2">Members who chose you, their shared reflections, and the daily prompts you publish.</p>
          <div className="flex gap-4 mt-6">
            <HeroStat label="Members" value={s.members ?? 0} />
            <HeroStat label="Shared reflections" value={s.shared ?? 0} />
            <HeroStat label="Active prompts" value={s.prompts ?? 0} />
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-8">
        <div className="flex gap-2 mb-6 border-b border-[#E5E0D8]">
          {[{ k: "reflections", l: "Shared Reflections", i: MessageCircle }, { k: "members", l: "My Members", i: Users }, { k: "prompts", l: "Daily Prompts", i: Sparkles }].map((t) => {
            const Icon = t.i;
            return (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`guide-tab-${t.k}`}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === t.k ? "border-[#144F75] text-[#144F75] font-semibold" : "border-transparent text-[#4A5D6B] hover:text-[#144F75]"}`}>
                <Icon size={15} /> {t.l}
              </button>
            );
          })}
        </div>

        {tab === "reflections" && (
          <div className="space-y-3" data-testid="guide-reflections">
            {data.reflections.length === 0 && <p className="text-[#4A5D6B] text-sm">No members have shared a reflection with you yet.</p>}
            {data.reflections.map((r) => {
              const m = pillarMeta(r.pillar);
              return (
                <div key={r.id} className="eyw-card p-5 flex items-start gap-4">
                  <span className="grid place-items-center h-11 w-11 rounded-xl text-white shrink-0" style={{ background: m.color }}>{m.label[0]}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-serif text-[#1A242B]">{r.user_name || "A member"}</h4>
                      <span className="text-xs text-[#8FA89B]">{fmtDate(r.date)}</span>
                    </div>
                    <p className="text-[#4A5D6B] text-sm mt-1">{r.text}</p>
                    <p className="text-xs text-[#8FA89B] mt-1.5">{m.label} · {r.category} · energy {r.energy}/5</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "members" && (
          <>
          <div className="eyw-card overflow-hidden hidden md:block" data-testid="guide-members">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F1EB] text-[#4A5D6B]"><tr><th className="text-left px-4 py-3">Member</th><th className="text-left px-4 py-3">Shared</th><th className="text-left px-4 py-3">Last reflection</th></tr></thead>
              <tbody className="divide-y divide-[#E5E0D8]">
                {data.members.map((m) => (
                  <tr key={m.id} data-testid={`guide-member-${m.id}`} className="hover:bg-[#F9F7F3]">
                    <td className="px-4 py-3 text-[#1A242B]">{m.name || m.email}</td>
                    <td className="px-4 py-3 text-[#4A5D6B]">{m.shared_count}</td>
                    <td className="px-4 py-3 text-[#8FA89B]">{m.last_reflection_date || "—"}</td>
                  </tr>
                ))}
                {data.members.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-[#4A5D6B]">No members have chosen you yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-3" data-testid="guide-members-mobile">
            {data.members.map((m) => (
              <div key={m.id} data-testid={`guide-member-card-${m.id}`} className="eyw-card p-4">
                <p className="text-[#1A242B] font-medium break-words">{m.name || m.email}</p>
                <div className="mt-2 flex justify-between text-sm text-[#4A5D6B]">
                  <span>Shared: {m.shared_count}</span>
                  <span className="text-[#8FA89B]">Last: {m.last_reflection_date || "—"}</span>
                </div>
              </div>
            ))}
            {data.members.length === 0 && <p className="px-4 py-8 text-center text-[#4A5D6B] text-sm eyw-card">No members have chosen you yet.</p>}
          </div>
          </>
        )}

        {tab === "prompts" && (
          <div data-testid="guide-prompts">
            <div className="eyw-card p-5 mb-5">
              <p className="eyw-label mb-2">Publish a daily prompt</p>
              <textarea rows={2} value={newPrompt.text} onChange={(e) => setNewPrompt((p) => ({ ...p, text: e.target.value }))}
                data-testid="guide-prompt-text" placeholder="A gentle question for your members to reflect on…" className="eyw-input w-full resize-none" />
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <select value={newPrompt.pillar} onChange={(e) => setNewPrompt((p) => ({ ...p, pillar: e.target.value }))} data-testid="guide-prompt-pillar" className="eyw-input text-sm">
                  {PILLARS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
                <button onClick={addPrompt} disabled={saving} data-testid="guide-prompt-add" className="eyw-btn !py-2 text-sm">
                  {saving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Publish
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {data.prompts.length === 0 && <p className="text-[#4A5D6B] text-sm">You haven't published any prompts yet.</p>}
              {data.prompts.map((p) => (
                <div key={p.id} data-testid={`guide-prompt-${p.id}`} className={`eyw-card p-4 flex items-center gap-3 ${p.active ? "" : "opacity-60"}`}>
                  <span className="text-xs px-2 py-0.5 rounded-full shrink-0" style={{ background: `${pillarMeta(p.pillar).color}22`, color: pillarMeta(p.pillar).color }}>{pillarMeta(p.pillar).label}</span>
                  <p className="flex-1 text-[#1A242B] text-sm">{p.text}</p>
                  <button onClick={() => toggle(p)} data-testid={`guide-prompt-toggle-${p.id}`} title={p.active ? "Active — click to hide" : "Hidden — click to activate"} className={p.active ? "text-[#5B8A72]" : "text-[#8FA89B]"}><Power size={16} /></button>
                  <button onClick={() => del(p.id)} data-testid={`guide-prompt-del-${p.id}`} className="text-red-500"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function HeroStat({ label, value }) {
  return (
    <div className="bg-white/10 rounded-xl px-4 py-2.5">
      <p className="text-2xl font-serif text-white">{value}</p>
      <p className="text-xs text-white/70">{label}</p>
    </div>
  );
}
